import { ReactNode, useCallback, useEffect, useState } from 'react';
import { Option } from './Option';
import { twMerge } from 'tailwind-merge';
import { useLocation, useNavigate } from 'react-router-dom';

export interface SubOptionProps {
  name: string;
  route: string;
  permission: string;
}

export interface OptionProps {
  name: string;
  description?: string;
  icon: ReactNode;
  subOptions: SubOptionProps[];
}

export interface MainNavigatorOptionProps {
  options: OptionProps[];
  isSuperAdmin?: boolean;
  userPermissions: string[];
  clientLogo?: string;
}

export const MainNavigator: React.FC<MainNavigatorOptionProps> = ({
  options,
  isSuperAdmin = false,
  userPermissions,
  clientLogo,
}) => {
  const [selectedOption, setSelectedOption] = useState<number>(0);
  const [isOpenSubOptions, setIsOpenSubOptions] = useState<boolean>(false);
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const setSelectOptionByPathname = useCallback(() => {
    const index = options.findIndex((option) => {
      return option.subOptions.some((subOption) => {
        return subOption.route.split('/')[1] === pathname.split('/')[1];
      });
    });
    setSelectedOption(index);
  }, [options, pathname]);

  const handleSelectedOption = (index: number) => {
    if (selectedOption === index) {
      setIsOpenSubOptions(!isOpenSubOptions);
      return;
    }
    setSelectedOption(index);
    setIsOpenSubOptions(true);
  };

  const handleSubOption = useCallback(
    (route: string) => {
      setIsOpenSubOptions(false);
      navigate(route);
    },
    [navigate]
  );

  useEffect(() => {
    setIsOpenSubOptions(false);
  }, []);

  const renderSubOptions = useCallback(() => {
    if (!options[selectedOption]?.subOptions) return <></>;
    return (
      <div className="mt-6">
        {options[selectedOption]?.subOptions.map((subOption) => {
          return (
            <button
              disabled={
                !userPermissions.includes(subOption.permission) && !isSuperAdmin
              }
              type="button"
              className="font-sans flex min-h-12 w-full items-center justify-start rounded-md px-4 py-4 text-sm text-slate-50 enabled:hover:bg-slate-900 disabled:opacity-25 disabled:cursor-not-allowed"
              key={subOption.name}
              onClick={() => handleSubOption(subOption.route)}
            >
              <span className="text-left">{subOption.name}</span>
            </button>
          );
        })}
      </div>
    );
  }, [handleSubOption, options, selectedOption]);

  useEffect(() => {
    setSelectOptionByPathname();
  }, [setSelectOptionByPathname]);

  return (
    <aside className="flex h-full min-h-screen w-max bg-slate-700">
      <section className="w-14">
        <div className="flex h-14 w-14 items-center justify-center bg-primary-500 p-2.5 bg-emerald-600">
          <img src={clientLogo ?? '/images/logo-col.png'} alt="webcol" />
        </div>
        <nav>
          {options.map((option: OptionProps, index) => (
            <Option
              option={option}
              key={option.name}
              onSelected={() => handleSelectedOption(index)}
              currentOption={selectedOption === index}
            />
          ))}
        </nav>
      </section>
      <div
        className={twMerge(
          'hidden h-full min-h-screen w-60 bg-slate-800 px-6 py-4 overflow-y-auto',
          `${isOpenSubOptions && 'block'}`
        )}
      >
        {selectedOption >= 0 && (
          <>
            <header>
              <h2 className="font-GilroySemibold text-lg font-normal text-slate-50">
                {options[selectedOption]?.name}
              </h2>
              <p className="font-sans mt-3 text-xs text-slate-400">
                {options[selectedOption]?.description}
              </p>
            </header>
            {renderSubOptions()}
          </>
        )}
      </div>
    </aside>
  );
};
